/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useRef, useState} from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import Loader from "components/Loader";
import clsx from "clsx";
import {UserGroupIcon} from "@heroicons/react/20/solid";
import {consoleLog, currencyNumberFormat} from "utils/utility";
import BookingPackageItemsDurations from "pages/Front/Booking/BookingPackageItems/BookingPackageItemsDurations";
import BookingPackageItemsWithKartingCount, {
    BookingPackageItemsWithKartingCountKartings,
} from "pages/Front/Booking/BookingPackageItems/BookingPackageItemsWithKartingCount";
import {AVAILABLE_DURATIONS_EVENTS, DEFAULT_HALL_ID, IS_UPSELL_ALLOWED} from "helpers/config";
import {PackageCategory} from "types/enums/PackageCategory";
import BookingAction from "store/booking/BookingAction";
import {PackagesItemsPerson} from "types/entities/PackagesItems";
import {usePackageItemKartingOptions} from "utils/packageUtils";
import useNavigation, {RouteName} from "utils/hooks/useNavigation";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import useSellerTimeslots from "utils/hooks/useSellerTimeslots";
import BookingSellerSelectedPackageNotice
    from "pages/Front/Booking/Seller/components/BookingSellerSelectedPackageNotice";

export interface BookingPackagesEventParams {}

interface BookingPackagesEventProps extends RouteComponentProps<BookingPackagesEventParams> {
    packageCategory: PackageCategory;
}

const SellerPackagesEvent = (props: BookingPackagesEventProps) => {
    const {t} = useTranslation();
    const bookingState = useTypedSelector(state => state.bookingState);
    const {historyPush} = useNavigation();
    const dispatch = useDispatch();
    const availablePackageItems = useTypedSelector(state => state.bookingState.bookingProcess.availablePackageItems);
    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const selectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);
    const seller_id = useTypedSelector(state => state.sessionState.session?.uid) || 1;
    const scrollRefTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefCustomTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefConfirmTimeslots = useRef<HTMLDivElement>(null);
    const {bookWithAutomaticTimeslot} = useSellerTimeslots(
        () => {},
        props.history,
        [scrollRefTimeslots, scrollRefCustomTimeslots, scrollRefConfirmTimeslots],
        () => {},
        seller_id
    );
    const personsCount: number = filter.adults + filter.children;
    const [chosenDuration, setChosenDuration] = useState<number | undefined>(240);
    // const [karting, setKarting] = useState<BookingPackageItemsWithKartingCountKartings>({
    //     withKarting: 0,
    //     withoutKarting: 0,
    // });
    const [karting, setKarting] = useState<BookingPackageItemsWithKartingCountKartings>({
        withKarting: personsCount,
        withoutKarting: 0,
    });
    const [allFilled, setAllFilled] = useState<boolean>(false);
    const [totalPrice, setTotalPrice] = useState<number>(0);
    const [defaultPackageWithKarting, setDefaultPackageWithKarting] = useState<PackagesItemsPerson | null>(null);
    const [defaultPackageWithoutKarting, setDefaultPackageWithoutKarting] = useState<PackagesItemsPerson | null>(null);
    const {setPackageItemHandler, getPackageItemForCombination} = usePackageItemKartingOptions();
    const [isKartingAvailable, setIsKartingAvailable] = useState<boolean>(true);

    const onNextStepHandler = () => {
        const availablePackages = bookingState?.bookingProcess?.availablePackageItems;
        const availablePackagesHasUpsell = availablePackages ? availablePackages.upsells.length > 0 : false;
        if (IS_UPSELL_ALLOWED && availablePackagesHasUpsell) {
            return historyPush(RouteName.UpsellsNew, "Packages");
        }
        bookWithAutomaticTimeslot(null, true);
    };

    /**
     * Fetches packages based on category and time slot.
     */
    const fetchPackagesByCategoryAndTimeSlot = () => {
        dispatch(
            BookingAction.fetchPackagesByCategoryAndTimeSlotForSeller(
                {
                    adults: filter.adults,
                    children: filter.children,
                    date: filter.date,
                    package_category_name: props.packageCategory,
                    hall_id: DEFAULT_HALL_ID,
                    activity_ids: filter.activity_ids.length === 0 ? [1] : filter.activity_ids,
                    timeslot_id: selectedTimeslot ? selectedTimeslot.id : null,
                },
                seller_id
            )
        ).then(r => {
            consoleLog("fetchPackagesByCategoryAndTimeSlot: res", r);
        });
    };

    const setDefaultPackages = () => {
        if (availablePackageItems) {
            const packageWithKarting = availablePackageItems?.packageItems?.adult?.find(item => item.duration === chosenDuration && item.karting);
            setDefaultPackageWithKarting(packageWithKarting ? packageWithKarting : null);
            const packageWithoutKarting = availablePackageItems?.packageItems?.adult?.find(item => item.duration === chosenDuration && !item.karting);
            setDefaultPackageWithoutKarting(packageWithoutKarting ? packageWithoutKarting : null);
        }
    };

    const setTotalPriceHandler = () => {
        if (!chosenDuration) {
            setTotalPrice(0);
        }

        const kartingPrice = defaultPackageWithKarting ? karting.withKarting * defaultPackageWithKarting.price : 0;
        const withoutKartingPrice = defaultPackageWithoutKarting ? karting.withoutKarting * defaultPackageWithoutKarting.price : 0;
        setTotalPrice(kartingPrice + withoutKartingPrice);
        setAllFilled(
            !!(chosenDuration && personsCount === karting.withoutKarting + karting.withKarting && (defaultPackageWithKarting || defaultPackageWithoutKarting))
        );
    };

    const setSelectedPackageWithCount = () => {
        if (defaultPackageWithKarting) {
            setPackageItemHandler(0, defaultPackageWithKarting.id, defaultPackageWithKarting?.person_type, karting.withKarting);
        }
        if (defaultPackageWithoutKarting) {
            setPackageItemHandler(1, defaultPackageWithoutKarting.id, defaultPackageWithoutKarting?.person_type, karting.withoutKarting);
        }
        if (!defaultPackageWithKarting && !defaultPackageWithoutKarting) {
            dispatch(BookingAction.resetSelectedPackageItems());
        }
    };

    const findClosestPackageItemDuration = (newDuration: number | undefined, newKartingState: number | undefined) => {
        let availableDurations = AVAILABLE_DURATIONS_EVENTS;
        if (newDuration !== undefined && availableDurations.includes(newDuration)) {
            availableDurations = [
                newDuration,
                ...availableDurations.filter((duration) => duration !== newDuration),
            ];
        }

        const newKarting = newKartingState === undefined ? true : newKartingState === 1;
        for (const duration of availableDurations) {
            const packageItem = getPackageItemForCombination(duration, newKarting, PackageItemPersonType.Adult);
            if (packageItem) {
                return {duration: duration, karting: packageItem.karting};
            }
        }

        return undefined;
    }

    const findClosestPackageItemForSelect = (targetDuration: number | undefined, kartingState: number | undefined) => {
        const priorityDuration = targetDuration && AVAILABLE_DURATIONS_EVENTS.includes(targetDuration) ? targetDuration : chosenDuration;
        const durationsToCheck = Array.from(new Set([priorityDuration, ...AVAILABLE_DURATIONS_EVENTS])).filter((d): d is number => !!d);
        const wantKarting = kartingState === undefined ? true : kartingState === 1;

        for (const duration of durationsToCheck) {
            const exactMatch = getPackageItemForCombination(duration, wantKarting, PackageItemPersonType.Adult);
            if (exactMatch) {
                return {duration, karting: exactMatch.karting, isKartingNegated: false};
            }

            const fallbackMatch = getPackageItemForCombination(duration, !wantKarting, PackageItemPersonType.Adult);
            if (fallbackMatch) {
                return {duration, karting: fallbackMatch.karting, isKartingNegated: true};
            }
        }

        return undefined;
    };

    const setChosenDurationHandler = (newDuration: number) => {
        const match = findClosestPackageItemForSelect(newDuration, undefined);

        if (!match) return;

        setChosenDuration(match.duration);
        setIsKartingAvailable(match.karting);

        if (match.isKartingNegated) {
            setKarting({
                withKarting: match.karting ? personsCount : 0,
                withoutKarting: match.karting ? 0 : personsCount,
            });
        }
    }

    useEffect(() => {
        fetchPackagesByCategoryAndTimeSlot();
    }, [filter.adults, filter.children]);

    useEffect(() => {
        setSelectedPackageWithCount();
        setTotalPriceHandler();
    }, [defaultPackageWithKarting, defaultPackageWithoutKarting, chosenDuration, karting]);

    useEffect(() => {
        setDefaultPackages();
    }, [availablePackageItems, chosenDuration, karting]);

    // default set after loading
    useEffect(() => {
        if (!availablePackageItems) return;

        const matchedPackage =
            findClosestPackageItemDuration(chosenDuration, isKartingAvailable ? 1 : 0) ||
            findClosestPackageItemDuration(chosenDuration, !isKartingAvailable ? 1 : 0);

        if (matchedPackage) {
            setChosenDuration(matchedPackage.duration);
            setIsKartingAvailable(matchedPackage.karting);
            setKarting({
                withKarting: matchedPackage.karting ? personsCount : 0,
                withoutKarting: matchedPackage.karting ? 0 : personsCount,
            });
        }
    }, [availablePackageItems]);

    if (!availablePackageItems) {
        return <Loader />;
    }

    return (
        <div className={"SellerPackagesEvent w-full"}>
            <BookingSellerSelectedPackageNotice selectedTimeslot={selectedTimeslot} />

            <div className={"border rounded-md lg:px-1 lg:py-1 xl:px-4 xl:py-3 bg-white shadow"}>
                <table className={"PackagesPersons w-full lg:border-separate lg:border lg:border-spacing-y-2 lg:border-spacing-x-2 lg:border-white"}>
                    <colgroup className={"hidden lg:table-column-group"}>
                        <col className="w-auto" />
                        <col className="w-auto" />
                        <col className="w-auto" />
                        <col className="min-w-[110px] w-auto" />
                    </colgroup>
                    <thead className={"hidden lg:table-header-group"}>
                        <tr>
                            <th className={"text-left font-bold text-lg text-black"}></th>
                            <th className={"font-bold text-center text-lg text-black"}>{t("Options")}</th>
                            <th className={"font-bold text-center text-lg text-black"}>{t("Select visit duration")}</th>
                            <th className={"font-bold text-right text-lg text-black"}>{t("Price")}</th>
                        </tr>
                    </thead>
                    <tbody className={"flex flex-col lg:table-row-group"}>
                        <tr
                            className={
                                // "BookingPackagesEventBasicDriverRow flex flex-col lg:table-row border-b lg:border-b-0 px-2 pt-3 pb-2 md:w-auto md:px-4 md:py-4 lg:px-0 lg:py-0"
                                "SellerBookingPackagesEventBasicDriverRow flex flex-col lg:table-row border-b-0 px-2 pt-3 pb-2 md:w-auto md:px-4 md:py-4 lg:px-0 lg:py-0"
                            }
                        >
                            <td
                                className={
                                    "align-middle py-2 lg:pr-2 lg:py-1 w-full lg:w-auto inline-flex flex-col md:flex-row items-center lg:table-cell border-b border-dashed md:border-b-0"
                                }
                            >
                                <div className={"flex flex-row items-center justify-start mb-2 md:mb-0"}>
                                    <UserGroupIcon className={clsx("h-7 w-7 xl:h-8 xl:w-8 flex-none mr-2", "text-teal-500")} />
                                    <div className={"flex flex-col items-center"}>
                                        <span className={"font-bold text-sm xl:text-base"}>{t("All persons")}</span>
                                    </div>
                                </div>
                            </td>
                            <td
                                className={"align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col md:flex-row md: items-center lg:table-cell"}
                            >
                                <h4 className={"lg:hidden font-bold text-base text-black mb-2 md:mb-0 md:mr-4 shrink-0 md:min-w-[210px]"}>
                                    {t("Ticket by hours")}
                                    <span>:</span>
                                </h4>
                                <BookingPackageItemsWithKartingCount
                                    className={"flex-row items-center justify-center md:justify-start lg:justify-center"}
                                    karting={karting}
                                    personCount={personsCount}
                                    setKarting={setKarting}
                                    isKartingDisable={!isKartingAvailable}
                                />
                            </td>
                            <td className={"align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col md:flex-row items-center lg:table-cell"}>
                                <h4 className={"lg:hidden font-bold text-base text-black mb-2 md:mb-0 md:mr-4 shrink-0 md:min-w-[210px]"}>
                                    {t("Select visit duration")}
                                    <span>:</span>
                                </h4>
                                <BookingPackageItemsDurations
                                    disabled1Hour={true}
                                    disabled2Hours={false}
                                    chosenDuration={chosenDuration}
                                    className={"justify-start sm:justify-center md:justify-start lg:justify-center"}
                                    enabled={true}
                                    personType={PackageItemPersonType.Adult}
                                    setChosenDuration={setChosenDurationHandler}
                                    isSeller={true}
                                />
                            </td>
                            <td
                                className={
                                    "align-middle py-2 lg:pl-2 lg:py-1 w-full lg:w-auto inline-flex flex-col md:flex-row items-center text-right lg:table-cell"
                                }
                            >
                                <h4 className={"text-left lg:hidden font-bold text-base text-black mb-2 md:mb-0 md:mr-4 shrink-0 md:min-w-[210px]"}>
                                    {t("Price")}
                                    <span>:</span>
                                </h4>
                                <div
                                    className={clsx(
                                        "flex flex-row items-center justify-end font-medium text-gray-700 text-sm xl:text-base",
                                        totalPrice && "underline underline-offset-4 lg:underline-offset-8"
                                    )}
                                >
                                    {totalPrice ? <span>{currencyNumberFormat(totalPrice)}</span> : <span>---</span>}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className={"flex flex-col items-end justify-center w-full mt-3 lg:mt-4"}>
                <div
                    className={
                        "flex flex-col items-center justify-center rounded-md bg-white text-sm font-semibold border shadow w-full md:min-w-[210px] md:w-auto"
                    }
                >
                    <h3 className={"font-bold text-xl text-black w-full px-4 py-3 border-b border-dashed flex justify-center md:justify-end"}>
                        {t("Total price")}
                    </h3>
                    <div className={"text-base font-bold text-gray-600 w-full px-4 py-3 flex justify-center md:justify-end"}>
                        {totalPrice ? <span>{currencyNumberFormat(totalPrice)}</span> : <span>---</span>}
                    </div>
                    <div className={"flex flex-col text-center md:text-right text-xs text-gray-500 w-full px-4 pb-3"}>
                        {defaultPackageWithoutKarting?.price && (
                            <span>
                                {t("without Karting")}: {currencyNumberFormat(defaultPackageWithoutKarting?.price)} {t("p.P.")}
                            </span>
                        )}
                        {defaultPackageWithKarting?.price && (
                            <span>
                                {t("with Karting")}: {currencyNumberFormat(defaultPackageWithKarting?.price)} {t("p.P.")}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withRouter(SellerPackagesEvent);
